import React, { useContext } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Space,
  Spin,
  Tag,
  Tooltip,
} from 'antd';

import Json from '@totem/components/common/Json';
import RuleDetailsContext from '@totem/components/rulesEngine/details/ruleDetailsContext';
import RuleGroupDisplay from '@totem/components/rulesEngine/report/ruleGroupDisplay';
import { RuleAction } from '@totem/components/rulesEngine/types';
import { isNotNull } from '@totem/utilities/common';

const STATUS_COLORS: Record<string, string> = {
  Active: 'success',
  Inactive: 'default',
};

const parseActionData = (data: string): object | null => {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

export const RuleDetailsExtra = () => {
  const { onRefresh } = useContext(RuleDetailsContext);

  return (
    <Space>
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
    </Space>
  );
};

const renderAction = (action: RuleAction, index: number) => {
  const parsedData = parseActionData(action.data);

  return (
    <Card
      key={action.id ?? `action-${index}`}
      size="small"
      title={action.type}
      style={{ marginBottom: 16 }}
    >
      <Descriptions column={1} size="small" bordered={false}>
        {isNotNull(action.claimKeys) && action.claimKeys.length > 0 && (
          <Descriptions.Item label="Claim Keys">
            {action.claimKeys.map((key) => (
              <Tag key={key}>{key}</Tag>
            ))}
          </Descriptions.Item>
        )}
        {isNotNull(action.cancelsKeys) && action.cancelsKeys.length > 0 && (
          <Descriptions.Item label="Cancels Keys">
            {action.cancelsKeys.map((key) => (
              <Tag key={key}>{key}</Tag>
            ))}
          </Descriptions.Item>
        )}
      </Descriptions>
      {parsedData ? (
        <Json value={parsedData} />
      ) : (
        action.data && <span>{action.data}</span>
      )}
    </Card>
  );
};

const RuleDetailsLayout = () => {
  const { rule, loading, notFound } = useContext(RuleDetailsContext);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (notFound || !rule) {
    return <Empty description="Rule not found" />;
  }

  const scope = rule.scope;
  const scopeNames = [
    scope?.organization?.name,
    scope?.region?.name,
    scope?.building?.name,
    scope?.controlSystem?.name,
  ].filter((name): name is string => !!name);

  return (
    <>
      <Card size="small" title="Details" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Name">{rule.name}</Descriptions.Item>
          <Descriptions.Item label="Domain">{rule.domain}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_COLORS[rule.status] ?? 'default'}>
              {rule.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Sequence">
            {rule.sequence}
          </Descriptions.Item>
          <Descriptions.Item label="Trigger">
            {(rule.trigger ?? []).join(', ')}
          </Descriptions.Item>
          <Descriptions.Item label="Scope Type">
            <Tag color={rule.scopeType === 'User' ? 'blue' : 'default'}>
              {rule.scopeType || 'System'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Scope" span={2}>
            {scopeNames.length > 0 ? scopeNames.join(' / ') : 'Global'}
          </Descriptions.Item>
          <Descriptions.Item label="Tags" span={2}>
            {isNotNull(rule.tags) && rule.tags.length > 0
              ? rule.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Created By" span={2}>
            {rule.createdBy?.name || '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Condition" style={{ marginBottom: 16 }}>
        <RuleGroupDisplay group={rule.group} />
      </Card>

      <Card size="small" title="Actions" style={{ marginBottom: 16 }}>
        {isNotNull(rule.actions) && rule.actions.length > 0 ? (
          rule.actions.map((action, index) => renderAction(action, index))
        ) : (
          <Empty description="No actions" />
        )}
      </Card>

      <Card size="small" title="Raw">
        <Json value={rule} />
      </Card>
    </>
  );
};

export default RuleDetailsLayout;
